/**
 * Re-authentication Dialog Component
 * @implements US-017 - Project Deletion (Re-authentication)
 * @implements TS-069 - Re-authentication UI
 *
 * Dialog for re-authenticating users before sensitive operations.
 * Supports both password-based and OAuth re-authentication flows.
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useMutation, gql } from '@apollo/client';
import { useAuthContext } from '@/providers';

/**
 * GraphQL mutation for verifying password
 * @implements TS-069 - Re-authentication UI
 */
const VERIFY_PASSWORD_MUTATION = gql`
  mutation VerifyPassword($input: VerifyPasswordInput!) {
    verifyPassword(input: $input)
  }
`;

/**
 * Error code mapping for user-friendly error messages
 * @implements TS-069 - Re-authentication UI
 */
const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: 'Session expired. Please log in again.',
  INVALID_PASSWORD: 'Incorrect password. Please try again.',
  ACCOUNT_LOCKED:
    'Your account has been locked due to too many failed attempts. Please try again later.',
  FORBIDDEN: 'You do not have permission to perform this action.',
};

/**
 * Extract user-friendly error message from GraphQL error
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check for known error codes in the message
    for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.includes(code)) {
        return message;
      }
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Pending action stored in localStorage for OAuth re-auth flow
 * @implements TS-069 - Re-authentication UI
 */
export interface PendingReauthAction {
  /** The type of action pending */
  action: 'delete_project' | 'other';
  /** The project ID (for project actions) */
  projectId?: string;
  /** Timestamp when the action was stored */
  timestamp: number;
}

/**
 * LocalStorage key for pending re-auth action
 */
export const PENDING_REAUTH_KEY = 'pending_reauth_action';

/**
 * Cookie name for re-auth verification
 */
export const REAUTH_VERIFIED_COOKIE = 'reauth_verified';

/**
 * Check if a valid reauth cookie exists
 * @returns The parsed cookie data or null
 */
export const getReauthCookie = (): {
  userId: string;
  verifiedAt: string;
} | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === REAUTH_VERIFIED_COOKIE && value) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }
  return null;
};

/**
 * Clear the reauth cookie
 */
export const clearReauthCookie = (): void => {
  document.cookie = `${REAUTH_VERIFIED_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

/**
 * Get pending re-auth action from localStorage
 * Only returns if action is recent (within 10 minutes)
 */
export const getPendingReauthAction = (): PendingReauthAction | null => {
  try {
    const stored = localStorage.getItem(PENDING_REAUTH_KEY);
    if (!stored) return null;

    const action = JSON.parse(stored) as PendingReauthAction;

    // Check if action is still valid (within 10 minutes)
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
    if (action.timestamp < tenMinutesAgo) {
      localStorage.removeItem(PENDING_REAUTH_KEY);
      return null;
    }

    return action;
  } catch {
    return null;
  }
};

/**
 * Clear pending re-auth action
 */
export const clearPendingReauthAction = (): void => {
  localStorage.removeItem(PENDING_REAUTH_KEY);
};

/**
 * Props for ReauthenticationDialog component
 */
export interface ReauthenticationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed (cancel) */
  onClose: () => void;
  /** Callback when re-authentication succeeds */
  onSuccess: () => void;
  /** Custom dialog title (optional) */
  title?: string;
  /** Custom description explaining why re-auth is needed (optional) */
  description?: string;
  /** The action type for pending re-auth (for OAuth flow) */
  pendingAction?: 'delete_project' | 'other';
  /** The project ID for project-related actions */
  projectId?: string;
}

/**
 * Re-authentication Dialog Component
 * @implements TS-069 - Re-authentication UI
 *
 * Displays a re-authentication dialog based on the user's authentication method:
 * - If user has a password (hasPassword=true): Shows password input form
 * - If user has no password (OAuth-only): Shows OAuth re-authentication button
 *
 * Features:
 * - Lock icon in header to indicate security
 * - Password visibility toggle
 * - Error handling for failed attempts and account lockout
 * - Loading state during verification
 * - Cancel button to abort the flow
 *
 * @example
 * ```tsx
 * <ReauthenticationDialog
 *   open={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   onSuccess={() => {
 *     setDialogOpen(false);
 *     proceedWithSensitiveAction();
 *   }}
 *   title="Confirm Your Identity"
 *   description="Re-enter your password to delete this project."
 * />
 * ```
 */
export const ReauthenticationDialog = ({
  open,
  onClose,
  onSuccess,
  title = 'Re-authenticate to Continue',
  description = 'For security, please verify your identity before proceeding with this action.',
  pendingAction,
  projectId,
}: ReauthenticationDialogProps) => {
  const { user } = useAuthContext();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has a password set
  const hasPassword = user?.hasPassword ?? false;

  // Verify password mutation
  const [verifyPassword, { loading: isVerifying }] = useMutation(
    VERIFY_PASSWORD_MUTATION,
    {
      onCompleted: (data) => {
        if (data.verifyPassword === true) {
          setError(null);
          setPassword('');
          onSuccess();
        } else {
          setError('Password verification failed. Please try again.');
        }
      },
      onError: (err) => {
        const message = getErrorMessage(err);
        setError(message);
      },
    },
  );

  /**
   * Handle password form submission
   */
  const handlePasswordSubmit = useCallback(() => {
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    setError(null);
    verifyPassword({
      variables: {
        input: { password },
      },
    });
  }, [password, verifyPassword]);

  /**
   * Handle Enter key press in password field
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !isVerifying) {
        handlePasswordSubmit();
      }
    },
    [handlePasswordSubmit, isVerifying],
  );

  /**
   * Handle dialog close - reset state
   */
  const handleClose = useCallback(() => {
    if (!isVerifying) {
      setPassword('');
      setError(null);
      onClose();
    }
  }, [isVerifying, onClose]);

  /**
   * Handle OAuth re-authentication redirect
   * @implements TS-069 - Re-authentication UI (OAuth flow)
   *
   * Redirects to the OAuth flow with a reauth parameter.
   * Before redirecting, stores the pending action in localStorage.
   * After successful OAuth, the callback will redirect back.
   */
  const handleOAuthReauth = useCallback(() => {
    // Store pending action in localStorage before redirecting
    // This allows the page to auto-proceed after OAuth returns
    if (pendingAction) {
      const pendingReauth: PendingReauthAction = {
        action: pendingAction,
        projectId,
        timestamp: Date.now(),
      };
      localStorage.setItem(PENDING_REAUTH_KEY, JSON.stringify(pendingReauth));
    }

    // Preserve the current URL for return after OAuth
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    // Redirect to GitHub OAuth with reauth flow indicator
    window.location.href = `/auth/github?flow=reauth&returnUrl=${returnUrl}`;
  }, [pendingAction, projectId]);

  /**
   * Toggle password visibility
   */
  const handleTogglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={isVerifying ? undefined : handleClose}
      aria-labelledby="reauth-dialog-title"
      aria-describedby="reauth-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id="reauth-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <LockIcon color="primary" />
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="reauth-dialog-description" sx={{ mb: 3 }}>
          {description}
        </DialogContentText>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {hasPassword ? (
          /* Password re-authentication form */
          <TextField
            autoFocus
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isVerifying}
            error={!!error}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    disabled={isVerifying}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          /* OAuth re-authentication option */
          <Box sx={{ textAlign: 'center' }}>
            <DialogContentText sx={{ mb: 2 }}>
              Since you signed up with GitHub, please re-authenticate using your
              GitHub account.
            </DialogContentText>
            <Button
              variant="contained"
              onClick={handleOAuthReauth}
              disabled={isVerifying}
              startIcon={<GitHubIcon />}
              fullWidth
              size="large"
              sx={{
                // GitHub branding colors
                backgroundColor: '#24292e',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#1b1f23',
                },
                '&:disabled': {
                  backgroundColor: '#6a737d',
                  color: '#ffffff',
                },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Re-authenticate with GitHub
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isVerifying} color="inherit">
          Cancel
        </Button>
        {hasPassword && (
          <Button
            onClick={handlePasswordSubmit}
            disabled={isVerifying || !password.trim()}
            variant="contained"
            color="primary"
          >
            {isVerifying ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Verify'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
